import React, { useEffect, useState } from 'react';
import { ORDER_STATUS_OPTIONS } from '../constants';
import { OutfitType, Order, OrderDraft, OrderStatus } from '../types';

export interface OrderFormValues extends OrderDraft {}

interface OrderFormProps {
  onSave: (values: OrderFormValues) => Promise<void> | void;
  onClose: () => void;
  initialOrder?: Order | null;
}

const normaliseDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }
  return value.slice(0, 10);
};

export const OrderForm: React.FC<OrderFormProps> = ({ onSave, onClose, initialOrder }) => {
  const [title, setTitle] = useState(initialOrder?.title ?? '');
  const [status, setStatus] = useState<OrderStatus>(initialOrder?.status ?? 'pending');
  const [outfitType, setOutfitType] = useState<OutfitType | ''>(initialOrder?.outfitType ?? '');
  const [negotiatedAmount, setNegotiatedAmount] = useState(
    initialOrder?.negotiatedAmount != null ? String(initialOrder.negotiatedAmount) : ''
  );
  const [paid, setPaid] = useState<boolean>(initialOrder?.paid ?? false);
  const [dueDate, setDueDate] = useState<string>(normaliseDateInput(initialOrder?.dueDate));
  const [deliveredAt, setDeliveredAt] = useState<string>(normaliseDateInput(initialOrder?.deliveredAt));
  const [notes, setNotes] = useState(initialOrder?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(initialOrder?.title ?? '');
    setStatus(initialOrder?.status ?? 'pending');
    setOutfitType(initialOrder?.outfitType ?? '');
    setNegotiatedAmount(initialOrder?.negotiatedAmount != null ? String(initialOrder.negotiatedAmount) : '');
    setPaid(initialOrder?.paid ?? false);
    setDueDate(normaliseDateInput(initialOrder?.dueDate));
    setDeliveredAt(normaliseDateInput(initialOrder?.deliveredAt));
    setNotes(initialOrder?.notes ?? '');
    setError(null);
    setSubmitting(false);
  }, [initialOrder]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Le titre de la commande est obligatoire.');
      return;
    }
    const amountValue = negotiatedAmount.trim();
    const parsedAmount = amountValue === '' ? null : Number(amountValue.replace(',', '.'));
    if (amountValue !== '' && Number.isNaN(parsedAmount)) {
      setError('Le montant doit être un nombre.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSave({
        id: initialOrder?.id,
        measurementId: initialOrder?.measurementId,
        title: trimmedTitle,
        status,
        outfitType: outfitType || null,
        negotiatedAmount: parsedAmount,
        paid,
        dueDate: dueDate ? dueDate : null,
        deliveredAt: deliveredAt ? deliveredAt : null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible d'enregistrer la commande.";
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white dark:bg-slate-800 shadow-xl">
        <div className="bg-brand-primary px-6 py-4 text-white shrink-0">
          <h2 className="text-lg font-semibold">
            {initialOrder ? 'Modifier la commande' : 'Nouvelle commande'}
          </h2>
        </div>
        <div className="max-h-[85vh] overflow-y-auto custom-scrollbar">
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          <div>
            <label htmlFor="order-title" className="block text-sm font-medium t-text-secondary">
              Intitulé de la commande
            </label>
            <input
              id="order-title"
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="t-input mt-1"
              placeholder="Ex. Boubou cérémonie"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium t-text-secondary">Statut</label>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value as OrderStatus)}
                className="t-input mt-1"
              >
                {ORDER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium t-text-secondary">Type de tenue</label>
              <select
                value={outfitType}
                onChange={(event) => setOutfitType(event.target.value as OutfitType | '')}
                className="t-input mt-1"
              >
                <option value="">Non spécifié</option>
                {Object.values(OutfitType).map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium t-text-secondary">Montant négocié (FCFA)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={negotiatedAmount}
                onChange={(event) => setNegotiatedAmount(event.target.value)}
                className="t-input mt-1"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="order-paid"
                type="checkbox"
                checked={paid}
                onChange={(event) => setPaid(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 text-brand-secondary focus:ring-brand-accent"
              />
              <label htmlFor="order-paid" className="text-sm t-text-secondary">
                Paiement effectué
              </label>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium t-text-secondary">Date de livraison prévue</label>
              <input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="t-input mt-1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium t-text-secondary">Date de livraison réelle</label>
              <input
                type="date"
                value={deliveredAt}
                onChange={(event) => setDeliveredAt(event.target.value)}
                className="t-input mt-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium t-text-secondary">Notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="t-input mt-1"
              placeholder="Détails supplémentaires, accessoires, tissu fourni, etc."
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-gray-200 dark:bg-slate-700 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200 transition hover:bg-gray-300 dark:hover:bg-slate-600"
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="rounded-md bg-brand-secondary px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-accent disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

export default OrderForm;
